import { ColDef, IServerSideDatasource, IServerSideGetRowsParams } from "ag-grid-community";
import { AxiosInstance } from "axios";
import {
  AOV_COLLECTION_COLUMN,
  COLLECTION_COLUMN,
  CURRENT_ACCOUNTKEY,
  // HOME_PAGE_JUMP,
  PAGE_SIZE,
  PORTFOLIO_COLUMN_STATE_KEY,
  PORTFOLIO_GROUP_EXPAND,
  ROLE,
  SUIT_COLLECTION_COLUMN,
} from "constant";
import { AuthDetail } from "types/auth";
import {
  defaultColumnsSort,
  dispatchCustomEvent,
  errorHandler,
  getCustomLayout,
  // newColumnDefaultShow,
  processFilterFromURL,
  toggleFilter,
  transformAgGridRequest,
} from "utils";
// import { PORTFOLIO_NEW_COLUMNS } from "constant/new-columns";
import { Dispatch, SetStateAction } from "react";
import { CustomColDef } from "types/app";
import { SelectAccount } from "types/summary";

export interface ServerSideDataSourceProps {
  axios: AxiosInstance;
  // callback?: () => void;
  authDetail: AuthDetail;
  embedded: boolean;
  setCollectionColumns: Dispatch<SetStateAction<string[]>>;
  setAOVCollectionColumns: Dispatch<SetStateAction<string[]>>;
  setExpandedGroups?: any;
  cavBuId?: number;
  ciscoCsmId?: string;
  fixedAccountId?: string;
  preSelected?: string[];
  setTotalSelect?: React.Dispatch<React.SetStateAction<SelectAccount[]>>;
  showAOV?: boolean;
}

const getDefaultHideColsByRole = (role: ROLE, embedded?: boolean) => {
  const sharedCols = [
    "accountSK", //#TO BE REMOVED# workaround to add this column since backend do not have the column
    "externalFilter",
    "externalSearch",
    "invoiceArr",
    "salesOwner",
    "verticalIndustryName",
    "personalTags",
    "subSalesCoverage",
    "contractTheatre",
    "salesLevel3",
    "salesLevel6",
    "companyName",
    "sitesPrimaryCluster",
    "sitesPrimaryWebexVersion",
    "managementTags",
    "renewalDate",
    "arOffPCCount",
    "conflictColor",
    "cgemFlag",
    "wholesaleFlag",
    "mtgRenewedSub",
    "calRenewedSub",
    "mtgPendingSub",
    "callPendingSub",
    "aemExpDate",
    "priorityIndicatorRank",
    "reseller",
    "resellerId",
    ...COLLECTION_COLUMN.filter((i) => i !== "deviceAmount3Y" && i !== "invoiceArrCloudWXCC"),
    ...AOV_COLLECTION_COLUMN.filter((i) => i !== "deviceAmount3Y" && i !== "aovCloudWXCC"),
    ...SUIT_COLLECTION_COLUMN,
  ];
  sharedCols.push("bpp");
  if (embedded) {
    sharedCols.push("attributes");
  }
  switch (role) {
    case ROLE.CALLING_MANAGER_ROLE:
      return [...sharedCols, "collabMapUploadedDate"];
    case ROLE.CCSE_ROLE:
      return [...sharedCols, "dsmName", "escalationEnggName"];
    default:
      return [
        ...sharedCols,
        "dsmName",
        "escalationEnggName",
        "ccseName",
        "ccseManagerName",
        "collabMapUploadedDate",
      ];
  }
};

const ServerSideDataSource = (props: ServerSideDataSourceProps): IServerSideDatasource => {
  let loaded = false;
  const getRows = (params: IServerSideGetRowsParams) => {
    const { startRow, rowGroupCols, groupKeys } = params.request;
    const { api, columnApi, successCallback, parentNode } = params;
    const pageSize = PAGE_SIZE;
    const pageNumber = startRow / pageSize;
    const postData = transformAgGridRequest(
      params.request,
      [
        {
          field: "successPlanFilter",
          operator: "eq",
          value: sessionStorage.getItem(CURRENT_ACCOUNTKEY) ?? props.fixedAccountId,
        },
      ],
      // props.ciscoCsmId === "globalcustomercare"
      //   ? [{ field: "cavBuId", operator: "eq", value: props.cavBuId }]
      //   : [
      //       { field: "cavBuId", operator: "eq", value: props.cavBuId },
      //       { field: "ciscoCsm", operator: "ne", value: "globalcustomercare" },
      //     ],
      props.setCollectionColumns,
      props.setAOVCollectionColumns,
      true
    );

    const groupType = rowGroupCols?.[0]?.field;
    if (rowGroupCols.length && groupKeys.length === 0) {
      props.axios
        // .get("/api/mock/v1/group", {
        .post(
          `/api/dataservice/v1/accounts/group`,
          {
            ...postData,
            groupType,
          },
          {
            headers: {
              "Page-Number": pageNumber,
              "Page-Size": pageSize,
            },
          }
        )
        .then(({ data, headers }) => {
          params?.successCallback(data, headers["total-elements"] || data.length);
          props?.setExpandedGroups?.((groups: string[]) => {
            groups.forEach((x) => {
              params.api.getRowNode(x)?.setExpanded(true);
            });
            return [];
          });
          // if expand group toggle enable, use toggle status to control group
          const columns = columnApi.getRowGroupColumns() || [];
          const savedCollapseGroup = localStorage.getItem(PORTFOLIO_GROUP_EXPAND);
          if (columns?.length && savedCollapseGroup) {
            params.api.forEachNode((node) => {
              node.setExpanded(savedCollapseGroup === "false" ? false : true);
            });
          }
        });
    } else {
      props.axios
        .post(
          "/api/portfolio/accounts",
          {
            ...postData,
            groupType,
            groupId:
              parentNode?.data?.groupId === null ? null : parentNode?.data?.groupId || groupKeys[0],
          },
          {
            headers: {
              "Page-Number": pageNumber,
              "Page-Size": 1000,
            },
          }
        )
        .then((res) => {
          const totalCount = res.headers["total-elements"];
          // const currentPage = res.headers["page-number"];
          // const totalPage = res.headers["total-page"];
          const { data } = res;
          // const currentDefs = params.columnApi.getAllColumns();
          if (data.length !== 0) {
            // hide columns that not in the response
            if (!loaded) {
              loaded = true;
              const keys = [...Object.keys(data[0]), "lifecycleStageByWorkload"];
              const visibleCols: string[] = [];
              const defaultHideCols = getDefaultHideColsByRole(
                props.authDetail.role,
                props.embedded
              );
              //#TO BE REMOVED# workaround to add this column since backend do not have the column
              const onlyFilterCols = [
                "accountSK",
                "conflictColor",
                "cgemFlag",
                "wholesaleFlag",
                "mtgRenewedSub",
                "calRenewedSub",
                "mtgPendingSub",
                "callPendingSub",
                "aemExpDate",
                "reseller",
                "resellerId",
                ...COLLECTION_COLUMN.filter(
                  (i) => i !== "deviceAmount3Y" && i !== "invoiceArrCloudWXCC"
                ),
                ...AOV_COLLECTION_COLUMN.filter(
                  (i) => i !== "deviceAmount3Y" && i !== "aovCloudWXCC"
                ),
                ...SUIT_COLLECTION_COLUMN,
              ];
              const defs = columnApi
                .getAllColumns()
                .map((x) => x.getColDef())
                // check toggle
                .filter((y) => toggleFilter(y, props.authDetail))
                .filter((def: ColDef & CustomColDef) => !def.exclude)
                .filter(
                  (def: ColDef & CustomColDef) =>
                    def.colId?.startsWith("external") ||
                    def.colId?.startsWith("banner") ||
                    keys.some((key) => {
                      return (def.authKey || def.colId || def.field) === key;
                    })
                )
                .sort((a, b) => defaultColumnsSort(a, b))
                .map((def: ColDef & CustomColDef) => {
                  if (
                    keys.some(
                      (key) =>
                        (def.colId?.indexOf(key) ?? -1) >= 0 ||
                        (def.authKey?.indexOf(key) ?? -1) >= 0
                    )
                  ) {
                    if (defaultHideCols.every((x) => x !== def.colId!)) {
                      visibleCols.push(def.colId!);
                    }
                    return {
                      ...def,
                      suppressColumnsToolPanel:
                        onlyFilterCols.some((x) => x === def.colId!) ||
                        def.colId?.startsWith("external") ||
                        def.colId === "priorityIndicatorRank",
                      //#TO BE REMOVED# workaround to add this column since backend do not have the column
                      suppressFiltersToolPanel:
                        (props.authDetail.role === ROLE.CISCO_WIDE_ROLE &&
                          def.colId === "accountSK") ||
                        def.colId === "priorityIndicatorRank",
                      hide:
                        !visibleCols.some((x) => x === def.colId!) ||
                        !onlyFilterCols.some((o) => o === def.colId) ||
                        def.colId === "priorityIndicatorRank",
                    };
                  } else {
                    return def;
                  }
                });
              api.setColumnDefs(defs, "api");
              const layout = getCustomLayout(defs, props.showAOV);
              setTimeout(() => {
                const columnToolPanel: any = api.getToolPanelInstance("columns");
                columnToolPanel?.setColumnLayout(layout);
              }, 100);
              const filtersToolPanel: any = api.getToolPanelInstance("filters");
              filtersToolPanel?.setFilterLayout(layout);
              const state = localStorage.getItem(PORTFOLIO_COLUMN_STATE_KEY);
              if (state) {
                const savedState = JSON.parse(state);
                const linkedCols = [
                  "mtgRenewedSub",
                  "calRenewedSub",
                  "mtgPendingSub",
                  "callPendingSub",
                  "reseller",
                  "resellerId",
                ];
                const newSavedState = savedState.filter(
                  (col: any) => !linkedCols.includes(col.colId)
                );
                // const newState = newColumnDefaultShow(
                //   PORTFOLIO_NEW_COLUMNS,
                //   newSavedState
                // );
                columnApi.setColumnState(newSavedState);
                localStorage.setItem(PORTFOLIO_COLUMN_STATE_KEY, JSON.stringify(newSavedState));
                if (props.embedded) {
                  columnApi.setColumnVisible("attributes", true);
                  columnApi.setColumnPinned("attributes", "left");
                  columnApi.moveColumn("attributes", 2);
                }
                // force to set to visiable
                columnApi.setColumnsVisible(["externalCheckbox", "accountId", "accountName"], true);
              } else {
                columnApi.setColumnsVisible(visibleCols, true);
                // Because the column will not order by alphabat, so sort it again
                columnApi.setColumnState(
                  columnApi
                    .getColumnState()
                    .sort((a, b) =>
                      defaultColumnsSort(
                        columnApi.getColumn(a.colId).getColDef(),
                        columnApi.getColumn(b.colId).getColDef()
                      )
                    )
                );
              }
              // const homeJump = localStorage.getItem(HOME_PAGE_JUMP);
              // if (homeJump) {
              //   const columnsToHandle = ["alertGroup"];
              //   if (homeJump === ROLE.PARTNER_ROLE) {
              //     columnsToHandle.push("partnerExpandPlanRiskAssessment");
              //   } else {
              //     columnsToHandle.push("expandPlanRiskAssessment");
              //   }
              //   columnApi.setColumnsVisible(columnsToHandle, true);
              //   columnApi.moveColumns(columnsToHandle, 3);
              //
              //   localStorage.removeItem(HOME_PAGE_JUMP);
              // }
              dispatchCustomEvent("COLUMN_INITIALED", { api });
              processFilterFromURL(params.api, params.columnApi, false, true);
            }

            api.hideOverlay();
            params.successCallback(data, totalCount);
            props.setTotalSelect?.((prv) => {
              prv.forEach((item) => {
                if (item.accountKey) {
                  const node = api.getRowNode(item.accountKey);
                  if (node) {
                    node.setSelected(true);
                  }
                }
              });
              return prv;
            });
            props.preSelected?.map((p) => {
              const node = api.getRowNode(p);
              if (node) {
                node.setSelected(true);
              }
            });
          } else {
            api.showNoRowsOverlay();
            successCallback([], 0);
          }
          if (!groupType)
            dispatchCustomEvent<{ currentPage: number; totalCount: number }>("DATA_LOADED", {
              currentPage: pageNumber,
              totalCount,
            });
          // if (props.callback) {
          //   console.log(1);
          //   props.callback();
          // }
        })
        .catch((e) => {
          // if (props.callback) {
          //   props.callback();
          // }
          // this should work but due to ag-grid bug, we have a work around below
          // work around
          console.error(errorHandler(e));
          successCallback([], 0);
          api.showNoRowsOverlay();
        });
    }
  };

  return {
    getRows,
  };
};

export default ServerSideDataSource;
